/* ==========================================================================
   LOCK IN — motion + product interaction layer
   No dependencies. IntersectionObserver + rAF only.
   Every effect is opt-in via data attributes and degrades to static content.

   Structure note: document-level listeners and observers are created once at
   module scope. `scan()` is idempotent and safe to re-run whenever the theme
   editor swaps a section in — it only picks up elements it has not seen.
   ========================================================================== */

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const supportsObserver = 'IntersectionObserver' in window;

  /** Elements already handed to an observer, so re-scans stay cheap. */
  const seen = new WeakSet();

  /* ------------------------------------------------------------------
     Scroll reveal
     Elements carrying [data-lk-reveal] or [data-lk-clip] fade/wipe in once.
     A parent with [data-lk-stagger] cascades its children automatically.
     ------------------------------------------------------------------ */

  function markVisible(el) {
    if (el.hasAttribute('data-lk-reveal')) el.setAttribute('data-lk-reveal', 'is-visible');
    if (el.hasAttribute('data-lk-clip')) el.setAttribute('data-lk-clip', 'is-visible');
  }

  const revealObserver =
    reduceMotion || !supportsObserver
      ? null
      : new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              markVisible(entry.target);
              revealObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
        );

  const scanReveal = () => {
    // Assign cascading delays before observing, so the first intersection
    // already carries its offset.
    for (const group of document.querySelectorAll('[data-lk-stagger]')) {
      const step = Number(group.dataset.lkStagger) || 90;
      const children = group.querySelectorAll('[data-lk-reveal], [data-lk-clip]');
      children.forEach((child, i) => {
        if (!child.style.getPropertyValue('--lk-reveal-delay')) {
          child.style.setProperty('--lk-reveal-delay', `${i * step}ms`);
        }
      });
    }

    for (const el of document.querySelectorAll('[data-lk-reveal], [data-lk-clip]')) {
      if (seen.has(el)) continue;
      seen.add(el);

      if (!revealObserver) {
        markVisible(el);
        continue;
      }

      revealObserver.observe(el);
    }
  };

  /* ------------------------------------------------------------------
     Parallax
     Translates media by a fraction of its own scroll progress. A single
     rAF loop drives every element currently in view.
     ------------------------------------------------------------------ */

  const parallaxActive = new Set();
  let parallaxTicking = false;

  const parallaxObserver =
    reduceMotion || !supportsObserver
      ? null
      : new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) parallaxActive.add(entry.target);
              else parallaxActive.delete(entry.target);
            }
            requestParallax();
          },
          { rootMargin: '20% 0px' }
        );

  const updateParallax = () => {
    parallaxTicking = false;
    const vh = window.innerHeight;

    for (const el of parallaxActive) {
      const rect = el.getBoundingClientRect();
      // -1 when the element is just below the fold, 1 when just above it.
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const strength = Number(el.dataset.lkParallax) || 12;
      el.style.transform = `translate3d(0, ${(progress * strength).toFixed(2)}%, 0)`;
    }
  };

  function requestParallax() {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(updateParallax);
  }

  const scanParallax = () => {
    if (!parallaxObserver) return;

    for (const el of document.querySelectorAll('[data-lk-parallax]')) {
      if (seen.has(el)) continue;
      seen.add(el);
      el.classList.add('lk-parallax');
      parallaxObserver.observe(el);
    }
  };

  /* ------------------------------------------------------------------
     Header scroll state
     Horizon already flips data-sticky-state, but a transparent header also
     needs to know whether the page has actually moved, so the underlay can
     fade in rather than pop.
     ------------------------------------------------------------------ */

  let headerTicking = false;

  const applyHeaderState = () => {
    headerTicking = false;
    const header = document.querySelector('header-component');
    if (!header) return;
    header.toggleAttribute('data-lk-scrolled', window.scrollY > 24);
  };

  const requestHeaderState = () => {
    if (headerTicking) return;
    headerTicking = true;
    requestAnimationFrame(applyHeaderState);
  };

  /* ------------------------------------------------------------------
     Variant chips (Lock In — Product section)
     The radios are presentation only; the value that actually ships is the
     hidden [name="id"] input that <product-form-component> reads. Keeping
     them separate lets the price, compare-at and button state follow the
     selection without re-rendering the section.
     ------------------------------------------------------------------ */

  const syncVariant = (input) => {
    const root = input.closest('.lk-product');
    if (!root) return;

    const host = root.querySelector('.lk-product__form-host');
    const variantInput = root.querySelector('[data-lk-variant-input]');
    if (variantInput) variantInput.value = input.value;

    const price = root.querySelector('[data-lk-price]');
    if (price && input.dataset.price) price.textContent = input.dataset.price;

    const compare = root.querySelector('[data-lk-compare]');
    if (compare) {
      const value = input.dataset.compare || '';
      compare.textContent = value;
      compare.toggleAttribute('hidden', value === '');
    }

    const button = root.querySelector('[data-lk-atc]');
    if (button && host) {
      const available = input.dataset.available === 'true';
      button.disabled = !available;
      const label = available ? host.dataset.addLabel : host.dataset.soldOutLabel;
      if (label) button.textContent = label;
    }
  };

  /* ------------------------------------------------------------------
     Size guide units
     The product description carries an imperial table and a metric table,
     both meant to be toggled by a size-chart app. With none installed they
     both render. CSS shows metric alone; this upgrades that to a switch.
     ------------------------------------------------------------------ */

  const UNIT_LABELS = { metric: 'CM', imperial: 'IN' };

  const scanUnitTables = () => {
    for (const rte of document.querySelectorAll('.rte')) {
      if (seen.has(rte)) continue;

      const metric = rte.querySelector('[data-unit-system="metric"]');
      const imperial = rte.querySelector('[data-unit-system="imperial"]');
      if (!metric || !imperial) continue;

      seen.add(rte);

      const group = document.createElement('div');
      group.className = 'lk-units';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', 'Measurement units');

      /** @param {'metric'|'imperial'} unit */
      const makeButton = (unit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lk-units__btn';
        button.textContent = UNIT_LABELS[unit];
        button.setAttribute('aria-pressed', String(unit === 'metric'));
        button.addEventListener('click', () => select(unit));
        return button;
      };

      /** @param {'metric'|'imperial'} unit */
      const select = (unit) => {
        for (const [table, name] of [
          [metric, 'metric'],
          [imperial, 'imperial'],
        ]) {
          table.classList.toggle('is-active', name === unit);
          table.classList.toggle('is-inactive', name !== unit);
        }
        for (const button of group.children) {
          button.setAttribute('aria-pressed', String(button.textContent === UNIT_LABELS[unit]));
        }
      };

      group.append(makeButton('metric'), makeButton('imperial'));
      metric.parentNode?.insertBefore(group, metric);
      select('metric');
    }
  };

  /* ------------------------------------------------------------------
     Boot — listeners bound once, scans repeatable.
     ------------------------------------------------------------------ */

  const scan = () => {
    scanReveal();
    scanParallax();
    scanUnitTables();
    applyHeaderState();
  };

  if (parallaxObserver) {
    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', requestParallax, { passive: true });
  }

  window.addEventListener('scroll', requestHeaderState, { passive: true });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!input.matches('[data-lk-chips] input[type="radio"]')) return;
    syncVariant(input);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  // Re-scan after the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', scan);
})();
